import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';

export interface OverrideSignatureDialogProps {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

export function OverrideSignatureDialog({
  open,
  submitting,
  onClose,
  onSubmit,
}: OverrideSignatureDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleClose = () => {
    if (submitting) return;
    setReason('');
    setReasonError(false);
    setSubmitError(false);
    onClose();
  };

  const handleSubmit = async () => {
    const text = reason.trim();
    if (text.length === 0) {
      setReasonError(true);
      return;
    }
    setSubmitError(false);
    try {
      await onSubmit(text);
      setReason('');
      setReasonError(false);
    } catch {
      setSubmitError(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={t('signature.overrideTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('signature.overrideTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {submitError && <Alert severity="error">{t('signature.saveFailed')}</Alert>}
          <TextField
            label={t('signature.overrideReason')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setReasonError(false);
            }}
            error={reasonError}
            helperText={reasonError ? t('signature.overrideReasonRequired') : undefined}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {t('signature.overrideSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
